import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Card, CircularProgress, Table, TableHead, TableBody, TableRow,
  TableCell, Chip, Button, Stack, Typography, Snackbar, Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { getData, putData, API_ENDPOINTS } from "../../services/apiService";
import HandlingTierEditDialog from "./HandlingTierEditDialog";
import {
  PRC_BG, PRC_PRIMARY_GRADIENT, PRC_PRIMARY_GRADIENT_HOVER, PRC_PRIMARY_SHADOW,
} from "./_pricingTheme";

const fmtRange = (lo, hi) => {
  const loStr = lo == null ? "0" : Number(lo).toString();
  const hiStr = hi == null ? "∞" : Number(hi).toString();
  return `${loStr} – ${hiStr} kg`;
};

const fmtINR = (n) => (n == null ? "—" : `₹${Number(n).toFixed(2)}`);

export default function HandlingTierTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getData(API_ENDPOINTS.pricingHandlingTiers);
      setRows(res?.message?.data || res?.result || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const submit = async (id, patch) => {
    const url = API_ENDPOINTS.pricingHandlingTierUpdate.replace(":id", id);
    try {
      await putData(url, patch);
      setToast({ kind: "success", message: `Handling tier updated` });
      setEditing(null);
      fetchRows();
      return { ok: true };
    } catch (err) {
      const data = err?.response?.data;
      const message = data?.message || err.message;
      setToast({ kind: "error", message });
      return { ok: false, message };
    }
  };

  const fullTruckRow = useMemo(() => rows.find((r) => r.is_full_truck), [rows]);
  const partialRows = useMemo(() => rows.filter((r) => !r.is_full_truck), [rows]);

  if (loading) {
    return <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress /></Box>;
  }
  if (error) {
    return (
      <Card elevation={0} sx={{ border: "1px solid #fca5a5", bgcolor: "#fee2e2", p: 2 }}>
        <Typography color="error">Handling tiers failed: {error}</Typography>
      </Card>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          mb: 2, p: 2, borderRadius: 2.5,
          border: "1px solid #BAE6FD", bgcolor: "#F0F9FF",
          display: "flex", alignItems: "center", gap: 1.25,
        }}
      >
        <InfoOutlinedIcon sx={{ color: "#0369A1" }} />
        <Box>
          <Typography sx={{ fontWeight: 700, color: "#0C4A6E", fontSize: 13.5 }}>
            {partialRows.length} weight band{partialRows.length === 1 ? "" : "s"}
            {fullTruckRow ? " · 1 full-truck row" : ""}
          </Typography>
          <Typography variant="caption" sx={{ color: "#0C4A6E" }}>
            Flat ₹ added to the per-package preFee. Full-truck handling is added once per booking.
          </Typography>
        </Box>
      </Box>

      <Card elevation={0} sx={{ border: `1px solid ${PRC_BG.cardBorder}`, borderRadius: 2.5, overflow: "hidden" }}>
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                "& .MuiTableCell-root": {
                  bgcolor: "#F0F9FF",
                  fontWeight: 800,
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  color: "#0369A1",
                },
              }}
            >
              <TableCell>Tier</TableCell>
              <TableCell>Weight band</TableCell>
              <TableCell>Charge (₹)</TableCell>
              <TableCell>Last updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {partialRows.map((row) => (
              <TableRow key={row.id} hover sx={{ "&:hover": { bgcolor: PRC_BG.rowHover } }}>
                <TableCell sx={{ verticalAlign: "top", py: 1.5 }}>
                  <Typography fontWeight={700} sx={{ color: "#0F172A" }}>{row.tier_name}</Typography>
                  <Typography variant="caption" sx={{ color: "#64748B", fontFamily: "monospace", display: "block", mt: 0.25 }}>
                    {row.tier_code}
                  </Typography>
                  {row.description && (
                    <Typography variant="body2" sx={{ color: "#475569", mt: 0.75, lineHeight: 1.45, maxWidth: 360 }}>
                      {row.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", py: 1.5, fontFamily: "monospace", color: "#334155" }}>
                  {fmtRange(row.min_weight_kg, row.max_weight_kg)}
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", py: 1.5 }}>
                  <Chip
                    label={fmtINR(row.charge_inr)}
                    sx={{
                      fontFamily: "monospace",
                      fontWeight: 800,
                      height: 28,
                      background: "linear-gradient(135deg, #DBEAFE, #BFDBFE)",
                      color: "#1E3A8A",
                      border: "1px solid #93C5FD",
                    }}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", py: 1.5, whiteSpace: "nowrap", fontSize: 11, color: "#64748B" }}>
                  <div>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : "—"}</div>
                  <div style={{ fontFamily: "monospace", marginTop: 2 }}>
                    {row.updated_by_admin_id || <span style={{ opacity: 0.6 }}>(seed)</span>}
                  </div>
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", py: 1.5 }} align="right">
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<EditIcon fontSize="small" />}
                    onClick={() => setEditing(row)}
                    sx={{
                      background: PRC_PRIMARY_GRADIENT,
                      textTransform: "none",
                      fontWeight: 700,
                      boxShadow: PRC_PRIMARY_SHADOW,
                      "&:hover": { background: PRC_PRIMARY_GRADIENT_HOVER },
                    }}
                  >
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}

            {fullTruckRow && (
              <TableRow
                hover
                sx={{
                  bgcolor: PRC_BG.fullTruck,
                  "&:hover": { bgcolor: "#FDE68A" },
                  borderTop: `2px solid ${PRC_BG.fullTruckBorder}`,
                }}
              >
                <TableCell sx={{ verticalAlign: "top", py: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LocalShippingIcon sx={{ color: "#B45309", fontSize: 18 }} />
                    <Typography fontWeight={700} sx={{ color: "#78350F" }}>
                      {fullTruckRow.tier_name}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" sx={{ color: "#92400E", fontFamily: "monospace", display: "block", mt: 0.25 }}>
                    {fullTruckRow.tier_code}
                  </Typography>
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", py: 1.5, fontFamily: "monospace", color: "#92400E" }}>
                  (any)
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", py: 1.5 }}>
                  <Chip
                    label={fmtINR(fullTruckRow.charge_inr)}
                    sx={{ fontFamily: "monospace", fontWeight: 800, height: 28, bgcolor: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D" }}
                  />
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", py: 1.5, fontSize: 11, color: "#92400E" }}>
                  {fullTruckRow.updatedAt ? new Date(fullTruckRow.updatedAt).toLocaleString() : "—"}
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", py: 1.5 }} align="right">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon fontSize="small" />}
                    onClick={() => setEditing(fullTruckRow)}
                    sx={{ textTransform: "none", fontWeight: 700, borderColor: "#B45309", color: "#92400E" }}
                  >
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <HandlingTierEditDialog
        item={editing}
        onClose={() => setEditing(null)}
        onSubmit={submit}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={toast?.kind || "success"} onClose={() => setToast(null)} sx={{ fontWeight: 600 }}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
